import React from 'react';
import type { BlockDefinition, BlockInstance } from './types';
import {
  iaNavBlock, iaHeroBlock, iaFeaturesBlock, iaProofBlock, iaStepsBlock,
  iaPricingBlock, iaCtaBlock, iaFooterBlock,
} from './blocks/ia/blocks';
import { homeHeroBlock } from './blocks/homeHero';
import { homeImageSectionBlock } from './blocks/homeImageSection';
import { homeServicesBlock } from './blocks/homeServices';
import { homeTabSectionBlock } from './blocks/homeTabSection';
import { homeCtaBlock } from './blocks/homeCta';
import { homeImpactBlock } from './blocks/homeImpact';
import { homeTestimonialBlock } from './blocks/homeTestimonial';
import { homeFormBlock } from './blocks/homeForm';
import { leadFormBlock } from './blocks/leadForm';
import { whitepaperHeroBlock } from './blocks/whitepaperHero';
import { whitepaperListBlock } from './blocks/whitepaperList';
import { privacyHeroBlock } from './blocks/privacyHero';
import { privacyContentBlock } from './blocks/privacyContent';
import { lifeGalleryBlock } from './blocks/lifeGallery';
import { pageHeroBlock } from './blocks/pageHero';
import { contactHeaderBlock } from './blocks/contactHeader';
import { enquirySectionBlock } from './blocks/enquirySection';
import { officeMapBlock } from './blocks/officeMap';
import { featureListBlock } from './blocks/featureList';
import { logoWallBlock } from './blocks/logoWall';
import { cardGridBlock } from './blocks/cardGrid';
import { pricingTableBlock } from './blocks/pricingTable';
import { trialCtaBlock } from './blocks/trialCta';
import { aboutHeroBlock } from './blocks/aboutHero';
import { aboutInfoBlock } from './blocks/aboutInfo';
import { aboutValuesBlock } from './blocks/aboutValues';
import { aboutApproachBlock } from './blocks/aboutApproach';
import { aboutTeamBlock } from './blocks/aboutTeam';
import { aboutStandoutBlock } from './blocks/aboutStandout';
import { aboutBuildBlock } from './blocks/aboutBuild';
import { templateHeroBlock } from './blocks/templateHero';
import { templateAdvantagesBlock } from './blocks/templateAdvantages';
import { templateProductsBlock } from './blocks/templateProducts';
import { videoHeroBlock } from './blocks/videoHero';
import { integrationsGridBlock } from './blocks/integrationsGrid';
import { featuredArticleBlock } from './blocks/featuredArticle';
import { articleGridBlock } from './blocks/articleGrid';
import { articleDetailBlock } from './blocks/articleDetail';
import { complianceCardsBlock } from './blocks/complianceCards';
import { bastionTemplateBlock } from './blocks/bastionTemplate';
import { statStripBlock } from './blocks/statStrip';
import { kBlocks } from './blocks/kathreftis/blocks';
import { kathreftisThemeBlocks } from './blocks/kathreftis-theme/blocks';
import { kathreftisV3Blocks } from './blocks/kathreftis-v3/blocks';

const registry = new Map<string, BlockDefinition>();

export function register(def: BlockDefinition): void {
  registry.set(def.type, def);
}

export function getBlock(type: string): BlockDefinition | undefined {
  return registry.get(type);
}

/** All registered block type names (for the admin block palette). */
export function blockTypes(): string[] {
  return [...registry.keys()];
}

/** Validate an ordered block array against the registry schemas. Returns the
 *  cleaned blocks (parsed props) and any per-block errors — used by the API on
 *  save/publish and by the importer. */
export function validateBlocks(blocks: BlockInstance[]): {
  ok: boolean;
  valid: BlockInstance[];
  errors: string[];
} {
  const valid: BlockInstance[] = [];
  const errors: string[] = [];
  blocks.forEach((b, i) => {
    const def = registry.get(b.type);
    if (!def) {
      errors.push(`block[${i}]: unknown type "${b.type}"`);
      return;
    }
    const parsed = def.schema.safeParse(b.props);
    if (!parsed.success) {
      errors.push(`block[${i}] (${b.type}): ${parsed.error.issues[0]?.message ?? 'invalid props'}`);
      return;
    }
    valid.push({ type: b.type, props: parsed.data });
  });
  return { ok: errors.length === 0, valid, errors };
}

// Built-in blocks. Phase 1 ports real cymmetri.com sections here, one clean
// styled-components block per Strapi section component.
register(homeHeroBlock);
register(homeImageSectionBlock);
register(homeServicesBlock);
register(homeTabSectionBlock);
register(homeCtaBlock);
register(homeImpactBlock);
register(homeTestimonialBlock);
register(homeFormBlock);
register(leadFormBlock);
register(whitepaperHeroBlock);
register(whitepaperListBlock);
register(privacyHeroBlock);
register(privacyContentBlock);
register(lifeGalleryBlock);
register(pageHeroBlock);
register(contactHeaderBlock);
register(enquirySectionBlock);
register(officeMapBlock);
register(featureListBlock);
register(logoWallBlock);
register(cardGridBlock);
register(pricingTableBlock);
register(trialCtaBlock);
register(aboutHeroBlock);
register(aboutInfoBlock);
register(aboutValuesBlock);
register(aboutApproachBlock);
register(aboutTeamBlock);
register(aboutStandoutBlock);
register(aboutBuildBlock);
register(templateHeroBlock);
register(templateAdvantagesBlock);
register(templateProductsBlock);
register(videoHeroBlock);
register(integrationsGridBlock);
register(featuredArticleBlock);
register(articleGridBlock);
register(articleDetailBlock);
register(complianceCardsBlock);
// Bastion tenant — text-map marker for the verbatim thebastion.io template
// (rendered by apps/web/src/bastion.ts, not the React block pipeline).
register(bastionTemplateBlock);
register(statStripBlock);
// IntelliAuth tenant — its own marketing blocks (referenced only by the
// IntelliAuth site's page content; Cymmetri/Bastion never render these).
register(iaNavBlock);
register(iaHeroBlock);
register(iaFeaturesBlock);
register(iaProofBlock);
register(iaStepsBlock);
register(iaPricingBlock);
register(iaCtaBlock);
register(iaFooterBlock);
// Kathrefits AI corporate-website blocks (k*) — a reusable component library
// (any tenant can reference them); the kathreftis site renders them bare.
for (const b of kBlocks) register(b);
for (const b of kathreftisThemeBlocks) register(b);
for (const b of kathreftisV3Blocks) register(b);

/** Render an ordered block array. Invalid props are rejected, not thrown. Each
 *  block is wrapped in a [data-reveal] element so the client can scroll-reveal
 *  it (fade-up), matching the legacy AOS animations. Identical on server and
 *  client, so hydration stays in sync. */
export function renderBlocks(blocks: BlockInstance[]): React.ReactNode[] {
  return blocks
    .map((b, i) => {
      const def = getBlock(b.type);
      if (!def) return null;
      const parsed = def.schema.safeParse(b.props);
      if (!parsed.success) return null;
      const Renderer = def.Renderer as React.ComponentType<any>;
      return React.createElement(
        'div',
        { key: i, 'data-reveal': '' },
        React.createElement(Renderer, parsed.data as object),
      );
    })
    .filter(Boolean) as React.ReactNode[];
}
